import { useCallback, useEffect, useState } from "react";
import SitePhotos from "@/components/SitePhotos";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import Field from "@/components/Field";
import Modal from "@/components/Modal";
import StatusBadge from "@/components/StatusBadge";
import { VariationForm, VARIATION_TONE } from "@/pages/tabs/VariationsTab";
import { fmtAUDex, fmtDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Sun, SunDim, Check, Clock3, Circle, FilePenLine, Trash2, ChevronDown, Copy } from "lucide-react";

const hasSolarDetails = (s) => !!(s && s.required && ((s.system_size_kw != null && s.system_size_kw !== "") || s.inverter || s.panels || s.frames));

function StageChip({ tone, children }) {
  const cls = {
    green: "border-emerald-200 bg-emerald-50 text-emerald-700",
    blue: "border-sky-200 bg-sky-50 text-sky-700",
    orange: "border-orange-200 bg-orange-50 text-orange-700",
    grey: "border-gray-200 bg-gray-100 text-gray-600",
  }[tone];
  return <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase ${cls}`} style={{ letterSpacing: "0.06em" }}>{children}</span>;
}

function SolarVariationSection({ project, dwelling, solar, linked, reloadVariations, refresh, readOnly }) {
  const [formMode, setFormMode] = useState(null); // null | "create" | "edit"
  const size = solar.system_size_kw;
  const prefillDesc = `${dwelling.name} Solar System${size ? ` — ${size}kW` : ""}`;

  const removeVar = async () => {
    if (!window.confirm(`Remove ${linked.code} (${fmtAUDex(linked.amount)} Excl. GST)?\n\nThe linked Solar Variation will be deleted and all Variation / contract totals updated.`)) return;
    try {
      await api.delete(`/projects/${project.id}/dwellings/${dwelling.id}/solar-variation`);
      toast.success(`${linked.code} removed — contract totals updated`);
      reloadVariations(); refresh();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="border-t border-gray-200 pt-4" data-testid={`solar-variation-section-${dwelling.id}`}>
      <div className="mb-2 text-[10px] font-bold uppercase text-gray-500" style={{ letterSpacing: "0.12em" }}>Solar Variation</div>
      {!linked ? (
        <>
          <p className="mb-2.5 rounded-sm border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] leading-relaxed text-amber-700" data-testid={`solar-variation-missing-${dwelling.id}`}>
            No Variation linked yet — the Solar system price must be raised as a contract Variation so it is not forgotten.
          </p>
          {!readOnly && (
            <button data-testid={`solar-create-variation-${dwelling.id}`} className="bdx-btn-primary w-full justify-center" onClick={() => setFormMode("create")}>
              <FilePenLine size={14} strokeWidth={1.9} /> Create Solar Variation
            </button>
          )}
        </>
      ) : (
        <div className="rounded-md border border-[#d9cde5] bg-[#faf8fc] px-3.5 py-3" data-testid={`solar-variation-card-${dwelling.id}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <span className="inline-flex items-center gap-1.5 rounded-md border border-[#d9cde5] bg-white px-2 py-1 font-mono-data text-xs font-semibold text-[#604184]" data-testid={`solar-variation-code-${dwelling.id}`}>
                <Sun size={11} strokeWidth={2} className="text-amber-500" />{linked.code}
              </span>
              <div className="mt-1.5 truncate text-[11px] text-gray-600" title={linked.description}>{linked.description}</div>
            </div>
            <div className="shrink-0 text-right">
              <div className="font-mono-data text-sm font-semibold text-gray-950" data-testid={`solar-variation-amount-${dwelling.id}`}>{fmtAUDex(linked.amount)}</div>
              <div className="text-[9px] font-semibold uppercase text-gray-400" style={{ letterSpacing: "0.06em" }}>Excl. GST</div>
              <div className="mt-1 flex justify-end"><StatusBadge status={linked.status} tone={VARIATION_TONE[linked.status]} /></div>
            </div>
          </div>
          {linked.claimed && <p className="mt-2 text-[10px] font-medium text-emerald-700">Claimed — amount and status are locked</p>}
          {!readOnly && (
            <div className="mt-3 flex gap-2 border-t border-gray-200 pt-2.5">
              <button data-testid={`solar-view-variation-${dwelling.id}`} className="bdx-btn-secondary flex-1 justify-center text-xs" onClick={() => setFormMode("edit")}>
                View / Edit Variation
              </button>
              {!linked.claimed && (
                <button data-testid={`solar-remove-variation-${dwelling.id}`} className="bdx-btn flex-1 justify-center border-red-200 bg-red-50 text-xs font-semibold text-red-700 hover:bg-red-100" onClick={removeVar}>
                  <Trash2 size={13} strokeWidth={1.7} /> Remove Solar Variation
                </button>
              )}
            </div>
          )}
        </div>
      )}
      {formMode && (
        <VariationForm project={project}
          variation={formMode === "edit" ? linked : null}
          prefill={formMode === "create" ? { description: prefillDesc } : undefined}
          source={formMode === "create" ? { source_type: "solar_system", source_dwelling_id: dwelling.id } : undefined}
          titleOverride={formMode === "create" ? `Create Solar Variation — ${dwelling.name}` : undefined}
          onClose={() => setFormMode(null)}
          onSaved={() => { setFormMode(null); reloadVariations(); refresh(); }} />
      )}
    </div>
  );
}

function CopySystemDialog({ project, dwelling, sources, onClose, onCopied }) {
  const [sourceId, setSourceId] = useState(sources[0]?.id || "");
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!sourceId) return;
    setPreview(null); setError("");
    api.get(`/projects/${project.id}/dwellings/${dwelling.id}/solar/copy-preview`, { params: { source_dwelling_id: sourceId } })
      .then((r) => setPreview(r.data))
      .catch((e) => setError(apiError(e)));
  }, [project.id, dwelling.id, sourceId]);

  const copy = async () => {
    if (!preview) return;
    if (preview.requires_confirmation) {
      const parts = [];
      if (preview.target.has_details) parts.push("its current Solar system details will be replaced");
      if (preview.target.linked_variation) parts.push(`its Draft Solar Variation ${preview.target.linked_variation.code} will be removed and replaced by a new Draft Variation`);
      if (!window.confirm(`${dwelling.name} already has Solar data — ${parts.join(" and ")}.\n\nContinue?`)) return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${project.id}/dwellings/${dwelling.id}/solar/copy`, { source_dwelling_id: sourceId, confirm_replace: !!preview.requires_confirmation });
      toast.success(`${dwelling.name}: Solar system copied from ${preview.source.name}${data.variation ? ` — ${data.variation.code} created as Draft` : ""}`);
      onCopied();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const Row = ({ label, value, testId }) => (
    <div className="flex items-start justify-between gap-4 py-2 text-sm"><dt className="text-gray-500">{label}</dt><dd className="text-right font-medium text-gray-900" data-testid={testId}>{value}</dd></div>
  );

  return (
    <Modal title={`Copy Solar System — ${dwelling.name}`} onClose={onClose}>
      <div data-testid={`solar-copy-dialog-${dwelling.id}`}>
        <Field label="Copy Solar System From">
          <select data-testid="solar-copy-source" className="bdx-input" value={sourceId} onChange={(e) => setSourceId(e.target.value)}>
            {sources.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </Field>
        {error && <p className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[11px] text-red-700" data-testid="solar-copy-error">{error}</p>}
        {preview && (
          <>
            <dl className="mt-3 divide-y divide-gray-100 rounded-md border border-gray-200 bg-[#faf9f8] px-3" data-testid="solar-copy-preview">
              <Row label="Source dwelling" value={preview.source.name} testId="solar-copy-preview-source" />
              <Row label="Target dwelling" value={preview.target.name} testId="solar-copy-preview-target" />
              <Row label="System size" value={preview.source.system_size_kw != null ? `${preview.source.system_size_kw}kW` : "—"} testId="solar-copy-preview-size" />
              <Row label="Frames" value={preview.source.frames ? "Yes" : "No"} />
              <Row label="Inverter" value={preview.source.inverter || "—"} testId="solar-copy-preview-inverter" />
              <Row label="Panel" value={preview.source.panels || "—"} testId="solar-copy-preview-panels" />
              <Row label="Solar Variation (Excl. GST)" value={preview.variation ? <span className="font-mono-data">{fmtAUDex(preview.variation.amount)} <span className="text-[10px] text-gray-500">→ new Draft</span></span> : <span className="text-gray-500">None on source — technical details only</span>} testId="solar-copy-preview-amount" />
              {preview.variation && <Row label="Date Raised (copied from source)" value={<span className="font-mono-data">{fmtDate(preview.variation.date_raised)}</span>} testId="solar-copy-preview-date-raised" />}
              {preview.variation && <Row label="New Variation description" value={<span className="text-xs">{preview.target_description}</span>} testId="solar-copy-preview-description" />}
            </dl>
            <p className="mt-3 text-[10px] leading-relaxed text-gray-400">Copies technical system details only. Solar Works stays Pending; Site Photos, documents, VAR number, approval and claim state are never copied.</p>
            {preview.blocked && <p className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[11px] text-red-700" data-testid="solar-copy-blocked">{preview.blocked}</p>}
            {!preview.blocked && preview.requires_confirmation && (
              <p className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-700" data-testid="solar-copy-replace-warning">
                {dwelling.name} already has Solar data{preview.target.linked_variation ? ` (${preview.target.linked_variation.code} Draft)` : ""} — copying replaces it after confirmation.
              </p>
            )}
          </>
        )}
        <div className="mt-4 flex justify-end gap-2 border-t border-gray-100 pt-3">
          <button type="button" data-testid="solar-copy-cancel" className="bdx-btn-secondary" onClick={onClose}>Cancel</button>
          <button type="button" data-testid="solar-copy-confirm" className="bdx-btn-primary" disabled={busy || !preview || !!preview.blocked} onClick={copy}><Copy size={14} strokeWidth={1.9} /> {busy ? "Copying…" : "Copy System"}</button>
        </div>
      </div>
    </Modal>
  );
}

function DwellingSolarLane({ project, dwelling, refresh, linkedVariation, reloadVariations, open, onToggle, copySources }) {
  const { readOnly } = useAuth();
  const solar = dwelling.solar || { required: false };
  const [copying, setCopying] = useState(false);
  const [form, setForm] = useState({
    system_size_kw: solar.system_size_kw ?? "",
    inverter: solar.inverter || "",
    panels: solar.panels || "",
    frames: !!solar.frames,
  });
  const [doneDate, setDoneDate] = useState(solar.completed_date || new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setForm({ system_size_kw: solar.system_size_kw ?? "", inverter: solar.inverter || "", panels: solar.panels || "", frames: !!solar.frames });
  }, [solar.system_size_kw, solar.inverter, solar.panels, solar.frames]);

  const patch = async (payload, msg) => {
    setBusy(true);
    try {
      await api.patch(`/projects/${project.id}/dwellings/${dwelling.id}/solar`, payload);
      toast.success(msg);
      refresh();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const setRequired = (val) => {
    if (solar.required === val) return;
    if (!val && linkedVariation) {
      if (!window.confirm(`Setting ${dwelling.name} to No Solar will also remove its linked Solar Variation ${linkedVariation.code} (${fmtAUDex(linkedVariation.amount)} Excl. GST).\n\nVariation and contract totals will be updated. Continue?`)) return;
      patch({ required: false, remove_linked_variation: true }, `${dwelling.name}: set to No Solar — ${linkedVariation.code} removed`)
        .then(() => reloadVariations());
      return;
    }
    patch({ required: val }, val ? `${dwelling.name}: solar enabled` : `${dwelling.name}: set to No Solar`);
  };

  const completed = solar.required && solar.works_completed;
  const laneTone = !solar.required ? "border-orange-200" : completed ? "border-emerald-200" : "border-sky-200";

  return (
    <div data-testid={`solar-lane-${dwelling.name.replace(/\s+/g, "-").toLowerCase()}`} className={`self-start rounded-lg border ${laneTone} bg-white shadow-sm`}>
      <button type="button" onClick={onToggle} aria-expanded={open} data-testid={`solar-card-toggle-${dwelling.id}`}
        className={`flex w-full items-center justify-between gap-3 bg-[#faf9f8] px-5 py-3.5 text-left transition-colors hover:bg-[#f3eff7] ${open ? "border-b border-gray-200" : ""}`}
        title={open ? `Collapse ${dwelling.name}` : `Expand ${dwelling.name}`}>
        <div className="flex min-w-0 items-center gap-2.5">
          {solar.required ? <Sun size={17} strokeWidth={1.8} className={completed ? "text-emerald-600" : "text-sky-600"} /> : <SunDim size={17} strokeWidth={1.8} className="text-orange-500" />}
          <span className="truncate font-heading text-sm font-bold uppercase text-gray-950" style={{ letterSpacing: "0.06em" }}>{dwelling.name}</span>
          {!open && solar.required && solar.system_size_kw != null && solar.system_size_kw !== "" && (
            <span className="shrink-0 font-mono-data text-[11px] font-medium text-gray-500" data-testid={`solar-summary-size-${dwelling.id}`}>{solar.system_size_kw}kW</span>
          )}
          {!open && linkedVariation && (
            <span className="hidden shrink-0 items-center gap-1 rounded border border-[#d9cde5] bg-[#f3eff7] px-1.5 py-0.5 font-mono-data text-[10px] font-semibold text-[#604184] sm:inline-flex" data-testid={`solar-summary-variation-${dwelling.id}`}>
              {linkedVariation.code} · {fmtAUDex(linkedVariation.amount)}
            </span>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2.5">
          {!solar.required ? <StageChip tone="orange">No Solar</StageChip>
            : completed ? <StageChip tone="green">Completed · {fmtDate(solar.completed_date)}</StageChip>
              : <StageChip tone="blue">Solar · Pending</StageChip>}
          <ChevronDown size={16} strokeWidth={2} className={`text-gray-400 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
        </div>
      </button>
      {open && (
      <div className="space-y-4 px-5 py-4">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="bdx-label mb-1.5">Solar Required?</div>
            <div className="flex gap-2">
              {[["No", false], ["Yes", true]].map(([label, val]) => (
                <button key={label} data-testid={`solar-required-${label.toLowerCase()}-${dwelling.id}`} disabled={readOnly || busy}
                  onClick={() => setRequired(val)}
                  className={`bdx-btn ${(!!solar.required) === val ? "border-[#765297] bg-[#f3eff7] font-semibold text-[#604184]" : "border-gray-300 bg-white text-gray-600 hover:bg-gray-50"}`}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          {!readOnly && copySources.length > 0 && (
            <button type="button" data-testid={`solar-copy-system-${dwelling.id}`} disabled={busy}
              className="inline-flex items-center gap-1.5 rounded-md border border-sky-300 bg-sky-50 px-3 py-2 text-[10px] font-bold uppercase text-sky-700 shadow-sm transition-colors hover:bg-sky-100" style={{ letterSpacing: "0.06em" }}
              title="Copy the Solar system details (and Draft Solar Variation) from another dwelling" onClick={() => setCopying(true)}>
              <Copy size={13} strokeWidth={2} /> Copy System
            </button>
          )}
        </div>
        {copying && (
          <CopySystemDialog project={project} dwelling={dwelling} sources={copySources} onClose={() => setCopying(false)}
            onCopied={() => { setCopying(false); reloadVariations(); refresh(); }} />
        )}

        {solar.required && (
          <>
            <div className="border-t border-gray-200 pt-4">
              <div className="mb-2 text-[10px] font-bold uppercase text-gray-500" style={{ letterSpacing: "0.12em" }}>Solar System Details</div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field label="System Size (kW)">
                  <input data-testid={`solar-size-${dwelling.id}`} type="number" step="0.1" min="0" className="bdx-input font-mono-data" disabled={readOnly}
                    value={form.system_size_kw} onChange={(e) => setForm({ ...form, system_size_kw: e.target.value })} placeholder="6.6" />
                </Field>
                <Field label="Frames">
                  <select data-testid={`solar-frames-${dwelling.id}`} className="bdx-input" disabled={readOnly}
                    value={form.frames ? "Yes" : "No"} onChange={(e) => setForm({ ...form, frames: e.target.value === "Yes" })}>
                    <option>No</option><option>Yes</option>
                  </select>
                </Field>
                <Field label="Inverter Brand & Model">
                  <input data-testid={`solar-inverter-${dwelling.id}`} className="bdx-input" disabled={readOnly}
                    value={form.inverter} onChange={(e) => setForm({ ...form, inverter: e.target.value })} />
                </Field>
                <Field label="Solar Panel Brand & Model">
                  <input data-testid={`solar-panels-${dwelling.id}`} className="bdx-input" disabled={readOnly}
                    value={form.panels} onChange={(e) => setForm({ ...form, panels: e.target.value })} />
                </Field>
              </div>
              {!readOnly && (
                <div className="mt-3 flex justify-end">
                  <button data-testid={`solar-details-save-${dwelling.id}`} className="bdx-btn-secondary" disabled={busy}
                    onClick={() => patch({
                      system_size_kw: form.system_size_kw === "" ? null : Number(form.system_size_kw),
                      inverter: form.inverter, panels: form.panels, frames: form.frames,
                    }, `${dwelling.name}: solar system details saved`)}>Save Details</button>
                </div>
              )}
            </div>

            <div className="border-t border-gray-200 pt-4">
              <div className="mb-2 flex items-center justify-between">
                <div className="text-[10px] font-bold uppercase text-gray-500" style={{ letterSpacing: "0.12em" }}>Solar Works</div>
                {completed
                  ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700"><Check size={13} /> Completed {fmtDate(solar.completed_date)}</span>
                  : <span className="inline-flex items-center gap-1 text-xs font-semibold text-sky-700"><Clock3 size={13} /> Pending</span>}
              </div>
              {!readOnly && !completed && (
                <div className="flex gap-2">
                  <input data-testid={`solar-complete-date-${dwelling.id}`} type="date" className="bdx-input" value={doneDate} onChange={(e) => setDoneDate(e.target.value)} />
                  <button data-testid={`solar-complete-btn-${dwelling.id}`} className="bdx-btn-primary shrink-0" disabled={busy}
                    onClick={() => patch({ works_completed: true, completed_date: doneDate || null }, `${dwelling.name}: solar works completed`)}>Solar Works Completed</button>
                </div>
              )}
            </div>

            <SolarVariationSection project={project} dwelling={dwelling} solar={solar} linked={linkedVariation}
              reloadVariations={reloadVariations} refresh={refresh} readOnly={readOnly} />
          </>
        )}
      </div>
      )}
    </div>
  );
}

export default function SolarTab({ project, refresh }) {
  const [variations, setVariations] = useState([]);
  const [openId, setOpenId] = useState(null); // accordion — all collapsed by default, one open at a time
  const loadVariations = useCallback(() => api.get(`/projects/${project.id}/variations`).then((r) => setVariations(r.data)).catch(() => {}), [project.id]);
  useEffect(() => { loadVariations(); }, [loadVariations]);

  return (
    <div data-testid="solar-tab" className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-[0_3px_12px_rgba(16,19,21,0.055)]">
      <div className="h-1 bg-[#765297]" />
      <div className="flex flex-col gap-4 border-b border-gray-200 bg-white px-6 py-5 sm:flex-row sm:items-center sm:justify-between lg:px-7">
        <div className="flex items-center gap-3">
          <div className="bdx-icon-tile h-10 w-10"><Sun size={18} strokeWidth={1.9} /></div>
          <div>
            <h2 className="font-heading text-lg font-bold text-gray-950">Solar Roadmap</h2>
            <p className="mt-0.5 text-xs text-gray-500">Independent per-dwelling solar delivery — does not affect project completion</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-4">
        <SitePhotos project={project} roadmap="solar" />
        <div className="flex flex-wrap items-center gap-4 text-[10px] font-semibold uppercase text-gray-500" data-testid="solar-roadmap-legend" style={{ letterSpacing: "0.08em" }}>
          <span className="inline-flex items-center gap-1.5"><Check size={12} className="text-emerald-600" /> Complete</span>
          <span className="inline-flex items-center gap-1.5"><Clock3 size={12} className="text-sky-600" /> Pending Solar</span>
          <span className="inline-flex items-center gap-1.5"><Circle size={9} className="text-orange-400" /> No Solar</span>
        </div>
        </div>
      </div>
      <div className="bdx-roadmap-grid px-5 py-8 sm:px-8 lg:px-10 lg:py-10">
        <div className="mx-auto grid w-full max-w-[980px] grid-cols-1 items-start gap-6 md:grid-cols-2">
          {project.dwellings.map((d) => (
            <DwellingSolarLane key={d.id} project={project} dwelling={d} refresh={refresh}
              linkedVariation={variations.find((v) => v.source_type === "solar_system" && v.source_dwelling_id === d.id)}
              reloadVariations={loadVariations}
              copySources={project.dwellings.filter((o) => o.id !== d.id && hasSolarDetails(o.solar)).map((o) => ({ id: o.id, name: o.name }))}
              open={openId === d.id} onToggle={() => setOpenId(openId === d.id ? null : d.id)} />
          ))}
        </div>
      </div>
    </div>
  );
}
